/**
 * AP-32 13.2 — **Die Belegmaske.** Ein Blatt mit Abschnitten, nicht Reiter:
 * Kopf · Positionen · Summen · Konditionen · Texte · Kette & Verlauf · Intern.
 *
 * Nach dem Festschreiben sind die Abschnitte 1–5 **sichtbar gesperrt**, nicht
 * ausgeblendet. Gespeichert wird abschnittsweise: Der Kopf schickt nur, was
 * sich geändert hat, die Positionen gehen als Ganzes (`PUT …/lines`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Ban, ClipboardCopy, ClipboardPaste, FileDown, FileMinus, FolderInput, FolderPlus, Lock, Save, Send, Trash2 } from 'lucide-react'
import { downloadFile, extractFilename } from '@/lib/download'
import { lesbarerFehler } from '../lib/binary-error'
import {
  DOCUMENT_RESPONSE_OF,
  DOCUMENT_TRANSFERS,
  NUMBERED_INSTALLMENT_KINDS,
  PRICE_MODES,
  TAX_PROCEDURES,
  belegTitel,
  documentKindsWith,
  documentPermission,
  erfuelltRecht,
  hatKopffeld,
  type DocumentKind,
  type PriceMode,
  type TaxProcedure,
} from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { pageWide } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { useAssistentDatensatz } from '@/lib/assistent/assistent.store'
import { documentsApi } from '../api/documents.api'
import { KindFields, planAusAnsicht, planZuEingabe, type KindFieldValues, type PlanZeile } from '../components/KindFields'
import { PositionGrid } from '../components/PositionGrid'
import { ClipboardBar } from '../components/ClipboardBar'
import { PartnerPicker } from '../components/PartnerPicker'
import { PaymentsBlock } from '../components/PaymentsBlock'
import { MarkdownField } from '../components/MarkdownField'
import { SendDialog } from '@/components/belege/SendDialog'
import { DeliveryAddressField } from '../components/DeliveryAddressField'
import { DeliveryBillingBlock } from '../components/DeliveryBillingBlock'
import { Eingaenge } from '@/components/eingang/Eingaenge'
import { BELEG_ZIEL } from '@/components/eingang/texte'
import { DeductionsDialog } from '../components/DeductionsDialog'
import { SupplierResponseBlock } from '../components/SupplierResponseBlock'
import { ARTGRUPPEN } from '../lib/artgruppen'
import { behalteOffen, erledigt, verwirfBeimSchliessen, verwirfSpaeter } from '../lib/vorlaeufig'
import {
  aendere,
  ausAblage,
  ausAnsicht,
  finde,
  flach,
  formatBetrag,
  formatDatum,
  fuegeGruppenEin,
  hatOffeneAnhaenge,
  leseZahl,
  rechne,
  wartendeDatei,
  zahlText,
  zuEingabe,
  type GridLine,
} from '../lib/grid'
import type { ClerkRef, ClipboardItem, ContactRef, DocumentDetail, DocumentHeaderPayload, KindInfo, PartnerRef, ProjectPositionRef, ProjectRef, UnitRef } from '../types/document.types'
import { bestaetige, frage, hinweis } from '@/lib/dialoge'

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60'
const karte = 'rounded-lg border border-border bg-card p-4 shadow-sm'
const label = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground'

/** Die Kopffelder als Formular — Zahlen als Text, wie sie getippt werden. */
interface Kopf {
  clientId: string | null
  partnerName: string
  partnerAddressLines: string
  contactPersonId: string | null
  projectId: string | null
  projectPositionId: string | null
  documentDate: string
  serviceDateFrom: string
  serviceDateTo: string
  dueDate: string
  subject: string
  customerReference: string
  headerText: string
  footerText: string
  title: string
  bodyText: string
  currency: string
  exchangeRate: string
  language: string
  priceMode: PriceMode
  taxProcedure: TaxProcedure
  taxNote: string
  paymentTermsDays: string
  discountDays: string
  discountPercent: string
  paymentMethod: string
  documentDiscountPercent: string
  notes: string
  // ── Nachtrag: Felder je Art ──
  commission: string
  incoterm: string
  incotermPlace: string
  deliveryTermsText: string
  deliveryDateType: string
  deliveryDate: string
  deliveryDateTo: string
  deliveryNote: string
  validUntil: string
  quoteDeadline: string
  paymentSchedule: PlanZeile[]
  // ── Nachtrag 2 ──
  printPrices: boolean
  printLetterhead: boolean
  installmentNo: string
  // ── Nachtrag 3 ──
  clerkUserId: string
  // ── Nachtrag 4 ──
  contactName: string
  deliveryAddress: string
}

function kopfAus(b: DocumentDetail): Kopf {
  return {
    clientId: b.clientId,
    partnerName: b.partnerName,
    partnerAddressLines: b.partnerAddressLines.join('\n'),
    contactPersonId: b.contactPersonId,
    projectId: b.projectId,
    projectPositionId: b.projectPositionId,
    documentDate: b.documentDate,
    serviceDateFrom: b.serviceDateFrom ?? '',
    serviceDateTo: b.serviceDateTo ?? '',
    dueDate: b.dueDate ?? '',
    subject: b.subject ?? '',
    customerReference: b.customerReference ?? '',
    headerText: b.headerText ?? '',
    footerText: b.footerText ?? '',
    title: b.title ?? '',
    bodyText: b.bodyText ?? '',
    currency: b.currency,
    exchangeRate: zahlText(b.exchangeRate, 6),
    language: b.language,
    priceMode: b.priceMode,
    taxProcedure: b.taxProcedure,
    taxNote: b.taxNote ?? '',
    paymentTermsDays: b.paymentTermsDays?.toString() ?? '',
    discountDays: b.discountDays?.toString() ?? '',
    discountPercent: zahlText(b.discountPercent ?? null, 2),
    paymentMethod: b.paymentMethod ?? '',
    documentDiscountPercent: zahlText(b.documentDiscountPercent ?? null, 2),
    notes: b.notes ?? '',
    commission: b.commission ?? '',
    incoterm: b.incoterm ?? '',
    incotermPlace: b.incotermPlace ?? '',
    deliveryTermsText: b.deliveryTermsText ?? '',
    deliveryDateType: b.deliveryDateType ?? '',
    deliveryDate: b.deliveryDate ?? '',
    deliveryDateTo: b.deliveryDateTo ?? '',
    deliveryNote: b.deliveryNote ?? '',
    validUntil: b.validUntil ?? '',
    quoteDeadline: b.quoteDeadline ?? '',
    paymentSchedule: planAusAnsicht(b.paymentSchedule),
    printPrices: b.printPrices,
    printLetterhead: b.printLetterhead,
    installmentNo: b.installmentNo?.toString() ?? '',
    clerkUserId: b.clerkUserId ?? '',
    contactName: b.contactName ?? '',
    deliveryAddress: (b.deliveryAddressJson?.lines ?? []).join('\n'),
  }
}

/** Nur geänderte Felder — ein Abschnitt, der nichts sendet, tastet nichts an. */
function kopfAenderungen(vorher: Kopf, jetzt: Kopf, preise: boolean): DocumentHeaderPayload {
  const p: DocumentHeaderPayload = {}
  const text = (k: keyof Kopf) => (jetzt[k] as string).trim() || null
  const zahl = (k: keyof Kopf) => {
    const n = leseZahl(jetzt[k] as string)
    return n === null || Number.isNaN(n) ? null : n
  }
  const geaendert = (k: keyof Kopf) =>
    k === 'paymentSchedule' ? JSON.stringify(vorher[k]) !== JSON.stringify(jetzt[k]) : vorher[k] !== jetzt[k]
  if (geaendert('clientId') && jetzt.clientId) p.clientId = jetzt.clientId
  if (geaendert('contactPersonId')) {
    p.contactPersonId = jetzt.contactPersonId
    // N-17: Der Ansprechpartner gehört zu einem Partner — beides zusammen, sonst bildet der Server die Anschrift nicht neu.
    if (jetzt.clientId) p.clientId = jetzt.clientId
  }
  if (geaendert('partnerName')) p.partnerName = jetzt.partnerName.trim()
  if (geaendert('partnerAddressLines')) p.partnerAddressLines = jetzt.partnerAddressLines.split('\n').map((z) => z.trim()).filter(Boolean)
  if (geaendert('projectId')) p.projectId = jetzt.projectId
  if (geaendert('projectPositionId')) p.projectPositionId = jetzt.projectPositionId
  // Kein Belegdatum (N-1) — der Server setzt es bei jeder Änderung auf heute.
  for (const k of ['serviceDateFrom', 'serviceDateTo', 'dueDate', 'deliveryDate', 'deliveryDateTo', 'validUntil', 'quoteDeadline'] as const) {
    if (geaendert(k)) p[k] = jetzt[k] || null
  }
  for (const k of ['subject', 'customerReference', 'headerText', 'footerText', 'title', 'bodyText', 'taxNote', 'notes', 'commission', 'incotermPlace', 'deliveryTermsText', 'deliveryNote'] as const) {
    if (geaendert(k)) p[k] = text(k)
  }
  if (geaendert('incoterm')) p.incoterm = (jetzt.incoterm || null) as DocumentHeaderPayload['incoterm']
  if (geaendert('deliveryDateType')) p.deliveryDateType = (jetzt.deliveryDateType || null) as DocumentHeaderPayload['deliveryDateType']
  if (geaendert('paymentSchedule')) p.paymentSchedule = planZuEingabe(jetzt.paymentSchedule)
  if (geaendert('printPrices')) p.printPrices = jetzt.printPrices
  if (geaendert('printLetterhead')) p.printLetterhead = jetzt.printLetterhead
  if (geaendert('installmentNo')) p.installmentNo = zahl('installmentNo')
  if (geaendert('clerkUserId')) p.clerkUserId = jetzt.clerkUserId || null
  if (geaendert('contactName')) p.contactName = text('contactName')
  if (geaendert('deliveryAddress')) {
    const zeilen = jetzt.deliveryAddress.split('\n').map((z) => z.trim()).filter(Boolean)
    p.deliveryAddressJson = zeilen.length ? { lines: zeilen } : null
  }
  if (geaendert('currency')) p.currency = jetzt.currency
  if (geaendert('exchangeRate')) p.exchangeRate = zahl('exchangeRate')
  if (geaendert('language')) p.language = jetzt.language
  if (geaendert('priceMode')) p.priceMode = jetzt.priceMode
  if (geaendert('taxProcedure')) p.taxProcedure = jetzt.taxProcedure
  if (geaendert('paymentTermsDays')) p.paymentTermsDays = zahl('paymentTermsDays')
  if (geaendert('discountDays')) p.discountDays = zahl('discountDays')
  if (geaendert('discountPercent')) p.discountPercent = zahl('discountPercent')
  if (geaendert('paymentMethod')) p.paymentMethod = jetzt.paymentMethod || null
  if (preise && geaendert('documentDiscountPercent')) p.documentDiscountPercent = zahl('documentDiscountPercent')
  return p
}

export default function DocumentPage() {
  const { id = '' } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const terms = useTerminology()
  const { user, hasModule } = useAuthStore()

  const [beleg, setBeleg] = useState<DocumentDetail | null>(null)
  // AP-18: Der Assistent weiß, welcher Beleg geöffnet ist („dieser Beleg").
  useAssistentDatensatz(
    beleg ? { art: 'document', id: beleg.id, bezeichnung: t('assistant.record.document', { kind: t(`documents.kinds.${beleg.kind}`), number: beleg.label, partner: beleg.partnerName }) } : null,
  )
  const [kopfBasis, setKopfBasis] = useState<Kopf | null>(null)
  const [kopf, setKopf] = useState<Kopf | null>(null)
  const [zeilen, setZeilen] = useState<GridLine[]>([])
  const [zeilenGeaendert, setZeilenGeaendert] = useState(false)
  const [arten, setArten] = useState<KindInfo[]>([])
  const [vorgabeSatz, setVorgabeSatz] = useState(20)
  // Nachtrag 3: Vorschläge für Einheiten und die Auswahl der Sachbearbeiter.
  const [einheiten, setEinheiten] = useState<UnitRef[]>([])
  // AP-39 H: hochgezählt, wenn die KI im Posteingang einen Eingang dieses Belegs gelesen hat.
  const [gelesen, setGelesen] = useState(0)
  /**
   * N-101: Die Positionen des verknüpften Projekts. Sie werden nachgeladen,
   * sobald ein Projekt steht — ohne Projekt gibt es nichts zu wählen.
   */
  const [kopfPositionen, setKopfPositionen] = useState<ProjectPositionRef[]>([])
  const [bearbeiter, setBearbeiter] = useState<ClerkRef[]>([])
  useEffect(() => {
    documentsApi
      .units()
      .then(({ data }) => setEinheiten(data.data))
      .catch(() => setEinheiten([]))
    documentsApi
      .clerks()
      .then(({ data }) => setBearbeiter(data.data))
      .catch(() => setBearbeiter([]))
  }, [])
  const kopfProjektId = kopf?.projectId ?? null
  useEffect(() => {
    if (!kopfProjektId) {
      setKopfPositionen([])
      return
    }
    documentsApi
      .projectPositions(kopfProjektId)
      .then(({ data }) => setKopfPositionen(data.data))
      .catch(() => setKopfPositionen([]))
  }, [kopfProjektId])
  const [ablage, setAblage] = useState<ClipboardItem[]>([])
  const [kontakte, setKontakte] = useState<ContactRef[]>([])
  const [projekte, setProjekte] = useState<ProjectRef[]>([])
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweise, setHinweise] = useState<string[]>([])
  // AP-39 Nachtrag 2 (5.2): Was die Übernahme zu sagen hat (Preise aus dem Angebot, Weggelassenes), bringt die
  // Navigation zum neuen Beleg mit — je Beleg einmal; danach aus dem Verlauf, damit Neuladen es nicht wiederholt.
  useEffect(() => {
    const mitgebracht = (location.state as { hinweise?: string[] } | null)?.hinweise
    setHinweise(mitgebracht ?? [])
    if (mitgebracht) navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- je Beleg einmal; das Leeren des Verlaufs ändert die Kennung nicht
  }, [id])
  const [laeuft, setLaeuft] = useState(false)
  const [uebernahmeOffen, setUebernahmeOffen] = useState(false)
  const [sendenOffen, setSendenOffen] = useState(false)
  const [abzugOffen, setAbzugOffen] = useState(false)

  const projekteVerfuegbar = hasModule('PROJECT_MGMT')

  const anwenden = useCallback((b: DocumentDetail) => {
    setBeleg(b)
    const k = kopfAus(b)
    setKopfBasis(k)
    setKopf(k)
    setZeilen(ausAnsicht(b.lines))
    setZeilenGeaendert(false)
  }, [])

  useEffect(() => {
    setFehler(null)
    documentsApi
      .get(id)
      .then(({ data }) => anwenden(data.data))
      .catch((err) => setFehler(fehlertext(err, t)))
    documentsApi
      .kinds()
      .then(({ data }) => {
        setArten(data.data)
        const satz = (data as unknown as { defaultTaxRate?: number }).defaultTaxRate
        if (typeof satz === 'number') setVorgabeSatz(satz)
      })
      .catch(() => undefined)
    documentsApi
      .clipboard()
      .then(({ data }) => setAblage(data.data))
      .catch(() => undefined)
  }, [id, anwenden, t])

  useEffect(() => {
    if (!beleg?.clientId) {
      setKontakte([])
      return
    }
    documentsApi
      .contacts(kopf?.clientId ?? beleg.clientId)
      .then(({ data }) => setKontakte(data.data))
      .catch(() => setKontakte([]))
  }, [beleg?.clientId, kopf?.clientId])

  useEffect(() => {
    if (!projekteVerfuegbar) return
    documentsApi
      .projects('')
      .then(({ data }) => setProjekte(data.data))
      .catch(() => undefined)
  }, [projekteVerfuegbar])

  const kopfAenderung = useMemo(
    () => (beleg && kopfBasis && kopf ? kopfAenderungen(kopfBasis, kopf, beleg.rights.prices) : {}),
    [beleg, kopfBasis, kopf],
  )
  // N-105: Ein vorläufiger Beleg ist ungespeichert, auch wenn in der Maske noch nichts geändert ist.
  const ungespeichert = !!beleg?.provisional || Object.keys(kopfAenderung).length > 0 || zeilenGeaendert
  // N-105: Verlässt die Maske einen vorläufigen Beleg ohne „Speichern", wird er verworfen.
  const vorlaeufigeId = beleg?.provisional ? beleg.id : null
  useEffect(() => {
    if (!vorlaeufigeId) return
    behalteOffen(vorlaeufigeId)
    const schliessen = () => verwirfBeimSchliessen(vorlaeufigeId)
    window.addEventListener('pagehide', schliessen)
    return () => {
      window.removeEventListener('pagehide', schliessen)
      verwirfSpaeter(vorlaeufigeId)
    }
  }, [vorlaeufigeId])
  // N-103: Eingefügt, aber noch nicht gespeichert — der Eintrag bleibt bis „Speichern" in der Ablage.
  const eingefuegt = useMemo(() => new Set(flach(zeilen).flatMap((x) => (x.zeile.ablage && !x.zeile.id ? [x.zeile.ablage.itemId] : []))), [zeilen])
  const offeneAblage = useMemo(() => ablage.filter((i) => !eingefuegt.has(i.id)), [ablage, eingefuegt])

  useEffect(() => {
    if (!ungespeichert) return
    const warnen = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warnen)
    return () => window.removeEventListener('beforeunload', warnen)
  }, [ungespeichert])

  const ergebnis = useMemo(
    () =>
      kopf
        ? rechne(zeilen, { priceMode: kopf.priceMode, taxProcedure: kopf.taxProcedure, documentDiscountPercent: leseZahl(kopf.documentDiscountPercent) ?? null }, vorgabeSatz)
        : null,
    [zeilen, kopf, vorgabeSatz],
  )

  if (!beleg || !kopf) {
    return <div className={pageWide}>{fehler ? <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div> : t('common.loading')}</div>
  }

  const festgeschrieben = !!beleg.postedAt
  const editierbar = beleg.rights.update
  const preise = beleg.rights.prices
  const f = beleg.capabilities
  const setzeKopf = (patch: Partial<Kopf>) => setKopf((k) => (k ? { ...k, ...patch } : k))
  // Nachtrag 6: Ohne Festschreibepflicht gibt es keinen Entwurf — der Beleg ist „Gespeichert".
  const statusText = beleg.provisional
    ? t('documents.statuses.NEW')
    : beleg.status === 'DRAFT' && !beleg.requirePosting
      ? t('documents.statuses.SAVED')
      : t(`documents.statuses.${beleg.status}`)

  /**
   * N-17: Die Anschrift entsteht beim Wechsel sofort — aus demselben Dienst, der
   * sie beim Speichern bilden würde. Scheitert die Vorschau, bleibt der Wechsel
   * trotzdem stehen; der Server bildet sie dann beim Speichern.
   */
  const uebernimmAnschrift = (clientId: string, contactPersonId?: string) =>
    documentsApi
      .partnerSnapshot(clientId, { kind: beleg.kind, language: kopf.language, ...(contactPersonId !== undefined && { contactPersonId }) })
      .then(({ data }) =>
        setzeKopf({
          partnerName: data.data.partnerName,
          partnerAddressLines: data.data.partnerAddressLines.join('\n'),
          contactPersonId: data.data.contactPersonId,
          contactName: data.data.contactName ?? '',
        }),
      )
      .catch((err) => setFehler(fehlertext(err, t)))

  const partnerWaehlen = (p: PartnerRef) => {
    setzeKopf({ clientId: p.id, partnerName: p.name, contactPersonId: null })
    void uebernimmAnschrift(p.id)
  }

  const kontaktWaehlen = (id: string | null) => {
    setzeKopf({ contactPersonId: id, contactName: kontakte.find((k) => k.id === id)?.name ?? '' })
    if (kopf.clientId) void uebernimmAnschrift(kopf.clientId, id ?? 'none')
  }

  const kindWerte: KindFieldValues = {
    incoterm: kopf.incoterm,
    incotermPlace: kopf.incotermPlace,
    deliveryTermsText: kopf.deliveryTermsText,
    deliveryDateType: kopf.deliveryDateType,
    deliveryDate: kopf.deliveryDate,
    deliveryDateTo: kopf.deliveryDateTo,
    deliveryNote: kopf.deliveryNote,
    validUntil: kopf.validUntil,
    quoteDeadline: kopf.quoteDeadline,
    serviceDateFrom: kopf.serviceDateFrom,
    serviceDateTo: kopf.serviceDateTo,
    paymentSchedule: kopf.paymentSchedule,
  }

  /**
   * Speichert, was sich geändert hat — erst der Kopf, dann die Positionen, danach die Fotos
   * und Dateien, die an den Zeilen warten (N-104). **Nur hier und beim Festschreiben wird der
   * Beleg gespeichert** (N-103): Keine andere Aktion der Maske speichert nebenbei.
   */
  const speichern = async (): Promise<DocumentDetail> => {
    let aktuell = beleg
    if (Object.keys(kopfAenderung).length) aktuell = (await documentsApi.update(beleg.id, kopfAenderung)).data.data
    if (zeilenGeaendert) aktuell = (await documentsApi.setLines(beleg.id, zuEingabe(zeilen, preise))).data.data
    const vorher = flach(zeilen)
    if (vorher.some((x) => hatOffeneAnhaenge(x.zeile))) {
      // Die Antwort hat dieselbe Reihenfolge wie das Gesendete — so findet jede Zeile ihre ID.
      const nachher = flach(ausAnsicht(aktuell.lines))
      if (nachher.length !== vorher.length) {
        anwenden(aktuell)
        throw new Error(t('documents.grid.attachmentsNotAssigned'))
      }
      for (const [i, { zeile }] of vorher.entries()) {
        const lineId = nachher[i].zeile.id
        if (!lineId || !hatOffeneAnhaenge(zeile)) continue
        if (zeile.fotoWeg && !zeile.fotoNeu) aktuell = (await documentsApi.deleteImage(beleg.id, lineId)).data.data
        if (zeile.fotoNeu) aktuell = (await documentsApi.uploadImage(beleg.id, lineId, zeile.fotoNeu)).data.data
        for (const fileId of zeile.dateienWeg ?? []) aktuell = (await documentsApi.deleteLineFile(beleg.id, lineId, fileId)).data.data
        // Nacheinander: Jede Antwort ist der ganze Beleg, parallel überschriebe eine die andere.
        for (const datei of zeile.dateienNeu ?? []) aktuell = (await documentsApi.uploadLineFile(beleg.id, lineId, datei)).data.data
      }
    }
    // N-105: Erst jetzt ist ein neuer Beleg angelegt — in den Listen, mit Bezeichnung und ggf. Nummer.
    if (aktuell.provisional) {
      aktuell = (await documentsApi.keep(beleg.id)).data.data
      erledigt(beleg.id)
    }
    anwenden(aktuell)
    // N-103: Eingefügte Einträge hat das Speichern aus der Ablage genommen.
    if (flach(zeilen).some((x) => x.zeile.ablage && !x.zeile.id)) {
      documentsApi
        .clipboard()
        .then(({ data }) => setAblage(data.data))
        .catch(() => undefined)
    }
    return aktuell
  }

  /**
   * N-103: Vor PDF, Kopieren, Umwandeln und Senden — bei ungespeicherten Änderungen fragt die
   * Maske, statt nebenbei zu speichern. `null`: Der Benutzer hat abgebrochen.
   */
  const gespeichertOderFragen = async (): Promise<DocumentDetail | null> => {
    if (!ungespeichert) return beleg
    if (!(await bestaetige(t('documents.unsavedConfirm'), { ok: t('documents.saveAndContinue') }))) return null
    return speichern()
  }

  /** N-104: Foto oder Datei an einer Zeile vormerken — gespeichert wird mit „Speichern". */
  const anhaengen = (key: string, patch: Partial<GridLine>) => {
    setZeilen((alt) => aendere(alt, key, patch))
    setZeilenGeaendert(true)
  }

  const mitLauf = async (aktion: () => Promise<void>) => {
    setFehler(null)
    setLaeuft(true)
    try {
      await aktion()
    } catch (err) {
      const lesbar = lesbarerFehler(err)
      // 7.4: Das Festschreiben nennt jedes fehlende Merkmal — als Liste, nicht als ein Satz.
      const fehlend = (lesbar as { response?: { data?: { missing?: unknown } } })?.response?.data?.missing
      setFehler(
        Array.isArray(fehlend) && fehlend.length
          ? `${t('documents.post.missing')} ${fehlend.map((k) => t(`documents.post.missingItems.${String(k)}`)).join(' · ')}`
          : fehlertext(lesbar, t),
      )
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * 11.2 — Entwurf: frisch mit Wasserzeichen. Ungespeichertes erst nach Rückfrage speichern (N-103),
   * sonst zeigte das PDF den alten Stand. N-79: auch nach dem Festschreiben (Schalter „Briefpapier im PDF").
   */
  const pdfOeffnen = () =>
    mitLauf(async () => {
      const b = editierbar || beleg.rights.notes ? await gespeichertOderFragen() : beleg
      if (!b) return
      const res = await documentsApi.pdf(b.id)
      const name = extractFilename(res.headers['content-disposition'] as string | undefined, `${b.label}.pdf`)
      downloadFile(res.data, name, 'application/pdf')
    })

  /** 7.5 — Nummer, Hash, Archiv. Ungespeichertes wird vorher gesichert, sonst schriebe man den alten Stand fest. */
  const festschreiben = () =>
    mitLauf(async () => {
      if (!(await bestaetige(t('documents.post.confirm')))) return
      const b = ungespeichert ? await speichern() : beleg
      anwenden((await documentsApi.post(b.id)).data.data)
    })

  /** 8.3 — Rechnungsarten bekommen einen Stornobeleg; die Maske wechselt zu ihm. */
  const stornieren = () =>
    mitLauf(async () => {
      if (!(await bestaetige(t(f.taxable ? 'documents.cancel.confirmWithDocument' : 'documents.cancel.confirm')))) return
      const { data } = await documentsApi.cancel(beleg.id)
      if (data.data.id !== beleg.id) navigate(`/correspondence/${data.data.id}`)
      else anwenden(data.data)
    })

  const gutschrift = () =>
    mitLauf(async () => {
      const { data } = await documentsApi.creditNote(beleg.id)
      navigate(`/correspondence/${data.data.id}`)
    })

  /** Zeilen-ID nach dem Speichern — neue Zeilen haben vorher keine. */
  const gespeicherteId = (b: DocumentDetail, key: string | null): string | null => {
    if (!key) return null
    const direkt = finde(ausAnsicht(b.lines), key)
    return direkt?.zeile.id ?? null
  }

  const kopieren = () =>
    mitLauf(async () => {
      const b = await gespeichertOderFragen()
      if (!b) return
      const ids = [...auswahl].map((k) => gespeicherteId(b, k)).filter((x): x is string => !!x)
      if (!ids.length) return
      const { data } = await documentsApi.copyToClipboard(b.id, ids)
      setAblage(data.data)
    })

  /**
   * N-103: Einfügen setzt die Positionen **ungespeichert** in die Maske. Der Server rechnet
   * sie nur vor (Preise, Hinweise, Prüfung der Herkunft); gespeichert und aus der Ablage
   * genommen werden sie mit „Speichern".
   */
  const einfuegen = (nachKey: string | null, itemIds?: string[], frisch = false) =>
    mitLauf(async () => {
      const ids = itemIds ?? offeneAblage.map((i) => i.id)
      if (!ids.length) return
      const { data } = await documentsApi.pastePreview(beleg.id, { itemIds: ids, refreshPrices: frisch })
      const neu = data.data.items.map((e) => ausAblage(e.position, e.itemId, e.images))
      setZeilen((alt) => fuegeGruppenEin(alt, nachKey, neu))
      setZeilenGeaendert(true)
      setHinweise(data.data.hints)
    })

  const uebernehmen = (ziel: DocumentKind) =>
    mitLauf(async () => {
      // Phase I: Eine Anzahlungs- oder Teilrechnung kann ein Prozentsatz der Quelle sein — leer heißt: die Positionen.
      let percent: number | undefined
      if ((ziel === 'DOWN_PAYMENT_INVOICE' || ziel === 'PARTIAL_INVOICE') && preise) {
        // N-22: Trägt die Quelle Zahlungstermine, schlägt die erste Zeile den Prozentsatz vor.
        const ersterTermin = beleg.paymentSchedule.find((e) => e.percent !== null)?.percent
        const vorschlag = ziel === 'DOWN_PAYMENT_INVOICE' ? (ersterTermin !== undefined ? zahlText(ersterTermin, 2) : '30') : ''
        const eingabe = await frage(t('documents.installments.percentPrompt'), vorschlag)
        if (eingabe === null) return
        const wert = leseZahl(eingabe)
        if (eingabe.trim() && (wert === null || Number.isNaN(wert))) {
          setFehler(t('documents.installments.percentInvalid'))
          return
        }
        percent = eingabe.trim() ? (wert as number) : undefined
      }
      const b = await gespeichertOderFragen()
      if (!b) return
      const lineIds = [...auswahl].map((k) => gespeicherteId(b, k)).filter((x): x is string => !!x)
      const { data } = await documentsApi.copy(b.id, {
        targetKind: ziel,
        ...(percent === undefined && lineIds.length && { lineIds }),
        ...(percent !== undefined && { percent }),
      })
      // AP-39 Nachtrag 2 (5.2): Preisanfrage → Bestellung sagt, was aus dem Angebot kam und was wegblieb.
      navigate(`/correspondence/${data.data.id}`, data.hints?.length ? { state: { hinweise: data.hints } } : undefined)
    })

  const loeschen = () =>
    mitLauf(async () => {
      if (!(await bestaetige(t('documents.confirmDelete')))) return
      await documentsApi.remove(beleg.id)
      navigate('/correspondence')
    })

  /** N-105: Ein neuer, nie gespeicherter Beleg wird verworfen — dafür braucht es kein Löschrecht. */
  const verwerfen = () =>
    mitLauf(async () => {
      if (!(await bestaetige(t('documents.discardConfirm'), { ok: t('documents.discard'), gefaehrlich: true }))) return
      erledigt(beleg.id)
      await documentsApi.discard(beleg.id)
      navigate('/correspondence')
    })

  const erstellbar = new Set(documentKindsWith('create', user?.permissions))
  const ziele = DOCUMENT_TRANSFERS[beleg.kind].filter((k) => erstellbar.has(k) && arten.find((a) => a.kind === k)?.isEnabled !== false)
  // Nachtrag 2: Wo eine Art Preise führen kann, werden sie auch erfasst — ob sie gedruckt
  // werden, entscheidet „Preise auf dem Ausdruck" am Beleg.
  const preiseDerArt = f.prices !== 'no'
  // Nachtrag 2: Ein individuelles Dokument nur aus Text hat keine Summen.
  const nurText = beleg.kind === 'CUSTOM' && !zeilen.some((z) => z.lineType === 'ARTICLE' || z.lineType === 'SUBTOTAL' || z.lineType === 'DEDUCTION')
  const nummerierteTeilrechnung = (NUMBERED_INSTALLMENT_KINDS as readonly string[]).includes(beleg.kind)
  const artName = t(`documents.kinds.${beleg.kind}`)

  return (
    <div className={pageWide}>
      {/* ── Kopfleiste ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link to="/correspondence" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {beleg.kind === 'CUSTOM' && beleg.title ? beleg.title : belegTitel(artName, beleg.installmentNo, beleg.language)}{' '}
              {!beleg.provisional && <span className="font-mono text-lg text-muted-foreground">{beleg.label}</span>}
            </h1>
            <p className="text-sm text-muted-foreground">
              {statusText}
              {beleg.paymentStatus !== 'NONE' && ` · ${t(`documents.payStatuses.${beleg.paymentStatus}`)}`}
              {/* Beim vorläufigen Beleg sagt es der Status schon („Neu — noch nicht gespeichert"). */}
              {ungespeichert && !beleg.provisional && <span className="ml-2 text-amber-600">● {t('documents.unsaved')}</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(editierbar || beleg.rights.notes) && (
            <button type="button" disabled={!ungespeichert || laeuft} onClick={() => void mitLauf(async () => void (await speichern()))} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
              <Save className="h-4 w-4" />
              {t('common.save')}
            </button>
          )}
          {beleg.rights.post && (
            <button type="button" disabled={laeuft} onClick={() => void festschreiben()} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
              <Lock className="h-4 w-4" />
              {t('documents.post.action')}
            </button>
          )}
          <button type="button" disabled={laeuft} onClick={() => void pdfOeffnen()} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card disabled:opacity-50">
            <FileDown className="h-4 w-4" />
            {t('documents.pdf.open')}
          </button>
          {beleg.rights.send && (
            <button
              type="button"
              disabled={laeuft}
              onClick={() =>
                void mitLauf(async () => {
                  // Ein Brief geht mit dem gespeicherten Stand hinaus — Ungesichertes nur nach Rückfrage (N-103).
                  if (editierbar && !(await gespeichertOderFragen())) return
                  setSendenOffen(true)
                })
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {t('documents.send.action')}
            </button>
          )}
          {beleg.rights.creditNote && (
            <button type="button" disabled={laeuft} onClick={() => void gutschrift()} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card disabled:opacity-50">
              <FileMinus className="h-4 w-4" />
              {t('documents.creditNote.action')}
            </button>
          )}
          {beleg.rights.cancel && (
            <button type="button" disabled={laeuft} onClick={() => void stornieren()} className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50">
              <Ban className="h-4 w-4" />
              {t('documents.cancel.action')}
            </button>
          )}
          {/* AP-35 Phase H (4.3): „Projekt aus Beleg" — Kopf und Positionen in einem Schritt. */}
          {(beleg.kind === 'OFFER' || beleg.kind === 'ORDER_CONFIRMATION') &&
            !beleg.projectId &&
            beleg.clientId &&
            hasModule('PROJECT_MGMT.CORE') &&
            erfuelltRecht('projects.project.manage', user?.permissions) && (
              <button
                type="button"
                disabled={laeuft}
                onClick={() =>
                  void mitLauf(async () => {
                    if (!(await bestaetige(t('documents.projectFromDocument.confirm', { project: terms.project })))) return
                    // Das Projekt entsteht aus dem gespeicherten Stand (N-103, N-105).
                    const b = await gespeichertOderFragen()
                    if (!b) return
                    const { data } = await documentsApi.projectFromDocument(b.id)
                    navigate(`/projects/${data.data.projectId}?tab=positions`)
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card disabled:opacity-50"
              >
                <FolderPlus className="h-4 w-4" />
                {t('documents.projectFromDocument.action', { project: terms.project })}
              </button>
            )}
          {ziele.length > 0 && (
            // Die Übernahme nimmt die markierten Zeilen mit — ein Klick hier hebt die Markierung nicht auf.
            <div className="relative" data-auswahl-behalten>
              <button type="button" onClick={() => setUebernahmeOffen((v) => !v)} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
                <FolderInput className="h-4 w-4" />
                {t('documents.actions.transfer')}
              </button>
              {uebernahmeOffen && (
                // N-75: Jede Art ist Ziel — die Kopie zuerst, danach gegliedert wie im Anlegen-Dialog.
                <ul className="absolute right-0 z-50 mt-1 max-h-[70vh] min-w-[14rem] overflow-y-auto rounded-md border border-border py-1 shadow-lg" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
                  {[
                    { key: 'copy', arten: ziele.filter((k) => k === beleg.kind) },
                    ...ARTGRUPPEN.map((g) => ({ key: g.key, arten: ziele.filter((k) => k !== beleg.kind && g.arten.includes(k)) })),
                  ]
                    .filter((g) => g.arten.length)
                    .map((g) => (
                      <li key={g.key}>
                        {g.key !== 'copy' && (
                          <div className="px-3 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t(`documents.kindGroups.${g.key}`)}</div>
                        )}
                        <ul>
                          {g.arten.map((k) => (
                            <li key={k}>
                              <button
                                type="button"
                                onClick={() => {
                                  setUebernahmeOffen(false)
                                  void uebernehmen(k)
                                }}
                                className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent"
                              >
                                {k === beleg.kind ? t('documents.actions.copySameKind') : t(`documents.kinds.${k}`)}
                                {auswahl.size > 0 && <span className="ml-1 text-xs text-muted-foreground">({t('documents.actions.selectedLines', { count: auswahl.size })})</span>}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}
          {beleg.provisional ? (
            <button type="button" disabled={laeuft} onClick={() => void verwerfen()} className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50">
              <Trash2 className="h-4 w-4" />
              {t('documents.discard')}
            </button>
          ) : (
            beleg.rights.delete && (
              <button type="button" onClick={() => void loeschen()} className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-2 text-sm text-destructive hover:bg-destructive/10">
                <Trash2 className="h-4 w-4" />
                {t('common.delete')}
              </button>
            )
          )}
        </div>
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
      {hinweise.length > 0 && (
        <div className="rounded-md bg-amber-100 px-4 py-3 text-sm text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
          {hinweise.map((h) => (
            <p key={h}>{t(h)}</p>
          ))}
        </div>
      )}
      {festgeschrieben && (
        <div className="rounded-md border border-border bg-muted/40 px-4 py-2 text-sm text-muted-foreground">
          {t('documents.postedBanner', { date: beleg.postedAt ? new Date(beleg.postedAt).toLocaleString(navigator.language) : '', name: beleg.postedByName ?? '' })}
          {beleg.contentHash && <span className="ml-2 font-mono text-xs">{t('documents.hash', { hash: beleg.contentHash.slice(0, 16) })}</span>}
        </div>
      )}
      {!festgeschrieben && !beleg.requirePosting && beleg.status !== 'CANCELLED' && (
        <div className="rounded-md border border-border bg-muted/40 px-4 py-2 text-sm text-muted-foreground">{t('documents.head.noPosting')}</div>
      )}

      {/* ── 1. Kopf ── */}
      <section className={karte}>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-3 lg:col-span-2">
            <div>
              <label className={label}>{f.partnerRole === 'supplier' ? t('documents.partner.supplier') : t('documents.partner.customer')}</label>
              <PartnerPicker
                disabled={!editierbar}
                value={{ id: kopf.clientId, name: kopf.partnerName }}
                role={f.partnerRole === 'any' ? undefined : f.partnerRole}
                onSelect={partnerWaehlen}
              />
              {/* Nachtrag 2: Beim individuellen Dokument darf der Empfänger auch ohne Stammdaten sein. */}
              {f.partnerRole === 'any' && !kopf.clientId && (
                <input
                  disabled={!editierbar}
                  value={kopf.partnerName}
                  onChange={(e) => setzeKopf({ partnerName: e.target.value })}
                  placeholder={t('documents.partner.freeRecipient')}
                  className={`${eingabe} mt-2`}
                  maxLength={300}
                />
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label}>{t('documents.fields.addressLines')}</label>
                <textarea disabled={!editierbar} rows={5} value={kopf.partnerAddressLines} onChange={(e) => setzeKopf({ partnerAddressLines: e.target.value })} className={eingabe} />
              </div>
              <div className="space-y-3">
                <div>
                  <label className={label}>{t('documents.fields.contact')}</label>
                  <select disabled={!editierbar || !kontakte.length} value={kopf.contactPersonId ?? ''} onChange={(e) => kontaktWaehlen(e.target.value || null)} className={eingabe}>
                    <option value="">—</option>
                    {kontakte.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.position ? `${k.name} · ${k.position}` : k.name}
                      </option>
                    ))}
                  </select>
                  {/* Nachtrag 4: „z. H." von Hand — auch wenn der Ansprechpartner nicht im Stamm steht. */}
                  <input
                    disabled={!editierbar}
                    value={kopf.contactName}
                    maxLength={200}
                    onChange={(e) => setzeKopf({ contactName: e.target.value })}
                    placeholder={t('documents.head.attnPlaceholder')}
                    aria-label={t('documents.head.attn')}
                    className={`${eingabe} mt-2`}
                  />
                  {/* Nachtrag 2: Welche Textbausteine gelten, entscheidet der Ansprechpartner (Sie/du). */}
                  {kopf.clientId && kopf.contactPersonId === beleg.contactPersonId && (
                    <p className="mt-1 text-xs text-muted-foreground">{t(`documents.head.formOfAddress.${beleg.formOfAddress}`)}</p>
                  )}
                </div>
                {hatKopffeld(beleg.kind, 'commission') && (
                  <div>
                    <label className={label}>{t('documents.head.commission', { project: terms.project })}</label>
                    <input disabled={!editierbar} value={kopf.commission} maxLength={200} onChange={(e) => setzeKopf({ commission: e.target.value })} className={eingabe} placeholder={t('documents.head.commissionPlaceholder')} />
                  </div>
                )}
                {projekteVerfuegbar && (
                  <div>
                    <label className={label}>{hatKopffeld(beleg.kind, 'commission') ? t('documents.head.linkedProject', { project: terms.project }) : t('documents.fields.project')}</label>
                    <select disabled={!editierbar} value={kopf.projectId ?? ''} onChange={(e) => setzeKopf({ projectId: e.target.value || null })} className={eingabe}>
                      <option value="">—</option>
                      {kopf.projectId && !projekte.some((p) => p.id === kopf.projectId) && <option value={kopf.projectId}>{beleg.projectNumber}</option>}
                      {projekte.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.projectNumber} · {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {projekteVerfuegbar && kopf.projectId && (
                  // N-101: Die Position des verknüpften Projekts. Leer heißt „der ganze
                  // Auftrag"; neue Zeilen erben sie wie das Projekt.
                  <div>
                    <label className={label}>{t('documents.head.linkedProjectPosition', { position: terms.position })}</label>
                    <select
                      disabled={!editierbar}
                      value={kopf.projectPositionId ?? ''}
                      onChange={(e) => setzeKopf({ projectPositionId: e.target.value || null })}
                      className={eingabe}
                    >
                      <option value="">—</option>
                      {kopf.projectPositionId && !kopfPositionen.some((p) => p.id === kopf.projectPositionId) && (
                        <option value={kopf.projectPositionId}>{[beleg.projectPositionNumber, beleg.projectPositionName].filter(Boolean).join(' · ')}</option>
                      )}
                      {kopfPositionen.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nummer ?? ''} · {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            {hatKopffeld(beleg.kind, 'deliveryAddress') && (
              <DeliveryAddressField
                kind={beleg.kind}
                value={kopf.deliveryAddress}
                onChange={(wert) => setzeKopf({ deliveryAddress: wert })}
                editierbar={editierbar}
                partner={{ id: kopf.clientId, name: kopf.partnerName }}
                eingabe={eingabe}
                label={label}
              />
            )}
          </div>
          <div className="grid content-start gap-3">
            <div>
              <label className={label}>{t('documents.fields.documentDate')}</label>
              {/* N-1: keine Eingabe — der Server setzt das Datum bei jeder Änderung und beim Festschreiben. */}
              <div className="w-full rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-foreground">{formatDatum(kopf.documentDate)}</div>
              {editierbar && <p className="mt-1 text-xs text-muted-foreground">{t('documents.head.dateAuto')}</p>}
            </div>
            <div>
              <label className={label}>{t('documents.fields.subject')}</label>
              <input disabled={!editierbar} value={kopf.subject} onChange={(e) => setzeKopf({ subject: e.target.value })} className={eingabe} />
            </div>
            <div>
              {/* Nachtrag 3: Sachbearbeiter — steht als „Bearbeitet von" im PDF. */}
              <label className={label}>{t('documents.fields.clerk')}</label>
              <select disabled={!editierbar} value={kopf.clerkUserId} onChange={(e) => setzeKopf({ clerkUserId: e.target.value })} className={eingabe}>
                <option value="">—</option>
                {kopf.clerkUserId && !bearbeiter.some((b) => b.id === kopf.clerkUserId) && <option value={kopf.clerkUserId}>{beleg.clerkName ?? '—'}</option>}
                {bearbeiter.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {/* Nachtrag 3: Angebot „Ihr Zeichen"; AB, Lieferschein und Rechnungsarten „Bestellnummer" des Kunden. */}
              <label className={label}>
                {hatKopffeld(beleg.kind, 'orderNumber')
                  ? t('documents.head.orderNumber')
                  : beleg.kind === 'OFFER'
                    ? t('documents.head.yourReference')
                    : t('documents.fields.customerReference')}
              </label>
              <input
                disabled={!editierbar}
                value={kopf.customerReference}
                onChange={(e) => setzeKopf({ customerReference: e.target.value })}
                className={eingabe}
                placeholder={hatKopffeld(beleg.kind, 'orderNumber') ? t('documents.head.orderNumberPlaceholder') : undefined}
              />
            </div>
            {nummerierteTeilrechnung && (
              <div>
                <label className={label}>{t('documents.head.installmentNo', { kind: artName })}</label>
                <input
                  disabled={!editierbar}
                  value={kopf.installmentNo}
                  onChange={(e) => setzeKopf({ installmentNo: e.target.value.replace(/\D/g, '').slice(0, 2) })}
                  className={eingabe}
                  inputMode="numeric"
                />
              </div>
            )}
            {beleg.kind === 'CUSTOM' && (
              <div>
                <label className={label}>{t('documents.fields.title')}</label>
                <input disabled={!editierbar} value={kopf.title} onChange={(e) => setzeKopf({ title: e.target.value })} className={eingabe} placeholder={t('documents.hints.customTitle')} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── AP-39 12.3: Eingänge aus dem Posteingang (Preisanfrage, Bestellung, Lieferschein) ── */}
      {/* N-105: Ein vorläufiger Beleg nimmt noch nichts an — er kann verworfen werden. */}
      {BELEG_ZIEL[beleg.kind] && !beleg.provisional && (
        <Eingaenge
          targetKind={BELEG_ZIEL[beleg.kind]!}
          targetId={beleg.id}
          // Ein unterschriebener Lieferschein ändert nur den Verrechnungsblock — offene Eingaben im Kopf bleiben.
          onGeaendert={() => void documentsApi.get(beleg.id).then((r) => setBeleg((alt) => (alt ? { ...alt, billing: r.data.data.billing } : alt)))}
          // AP-39 H: An Preisanfrage und Bestellung liest die KI Werte — danach den Abschnitt darunter neu laden.
          mitWerten={!!DOCUMENT_RESPONSE_OF[beleg.kind]}
          onGelesen={() => setGelesen((n) => n + 1)}
        />
      )}

      {/* ── AP-39 10.5: Antwort des Lieferanten — nicht Inhalt des Belegs, darum auch nach dem Festschreiben erfassbar ── */}
      {DOCUMENT_RESPONSE_OF[beleg.kind] && !beleg.provisional && (
        <SupplierResponseBlock
          documentId={beleg.id}
          kind={DOCUMENT_RESPONSE_OF[beleg.kind]!}
          // Nicht `rights.update`: Das gilt dem Beleginhalt und endet mit dem Festschreiben.
          darfSchreiben={erfuelltRecht(documentPermission(beleg.kind, 'update'), user?.permissions)}
          preise={preise}
          einheiten={einheiten}
          neuLaden={gelesen}
          // AP-39 Nachtrag 2 (5.): derselbe Weg wie „Umwandeln in → Bestellung" — mit den markierten Positionen.
          bestellen={beleg.kind === 'INQUIRY' && ziele.includes('PURCHASE_ORDER') ? { markiert: auswahl.size, laeuft, onClick: () => void uebernehmen('PURCHASE_ORDER') } : undefined}
        />
      )}

      {/* ── 2. Positionen ── */}
      {f.lines !== 'no' && (
        <section className="space-y-2">
          {/* Einfügen, Kopieren und die Dialoge arbeiten mit der Markierung (N-64 neu). */}
          <div className="flex flex-wrap items-center gap-2" data-auswahl-behalten>
            <h2 className="mr-auto text-lg font-semibold">{t('documents.sections.lines')}</h2>
            {editierbar && preise && (beleg.kind === 'PARTIAL_INVOICE' || beleg.kind === 'FINAL_INVOICE') && (
              <button
                type="button"
                disabled={laeuft}
                onClick={() =>
                  void mitLauf(async () => {
                    // Abzüge hängen am gespeicherten Stand — Ungesichertes zuerst sichern.
                    if (ungespeichert) await speichern()
                    setAbzugOffen(true)
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-card disabled:opacity-50"
              >
                {t('documents.deductions.action')}
              </button>
            )}
            <button type="button" disabled={!auswahl.size || laeuft} onClick={() => void kopieren()} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm hover:bg-card disabled:opacity-50" title="Strg+C">
              <ClipboardCopy className="h-4 w-4" />
              {t('documents.clipboard.copy')}
            </button>
            {editierbar && (
              <button type="button" disabled={!offeneAblage.length || laeuft} onClick={() => void einfuegen([...auswahl].at(-1) ?? null)} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm hover:bg-card disabled:opacity-50" title="Strg+V">
                <ClipboardPaste className="h-4 w-4" />
                {t('documents.clipboard.paste')}
              </button>
            )}
          </div>

          <ClipboardBar
            items={offeneAblage}
            waehrung={beleg.currency}
            kannEinfuegen={editierbar && !laeuft}
            preise={preise}
            onPaste={(ids, frisch) => void einfuegen([...auswahl].at(-1) ?? null, ids, frisch)}
            onRemove={(itemId) => void mitLauf(async () => setAblage((await documentsApi.removeClipboardItem(itemId)).data.data))}
            onClear={() =>
              void mitLauf(async () => {
                // Eingefügte, noch ungespeicherte Einträge bleiben — das Speichern braucht sie (N-103).
                if (!eingefuegt.size) return setAblage((await documentsApi.clearClipboard()).data.data)
                let rest = ablage
                for (const i of offeneAblage) rest = (await documentsApi.removeClipboardItem(i.id)).data.data
                setAblage(rest)
              })
            }
          />

          <PositionGrid
            zeilen={zeilen}
            onChange={(neu) => {
              setZeilen(neu)
              setZeilenGeaendert(true)
            }}
            editierbar={editierbar}
            preise={preise}
            preiseDerArt={preiseDerArt}
            waehrung={beleg.currency}
            partnerId={beleg.clientId}
            sprache={beleg.language}
            kopf={{ priceMode: kopf.priceMode, taxProcedure: kopf.taxProcedure, documentDiscountPercent: leseZahl(kopf.documentDiscountPercent) ?? null }}
            vorgabeSatz={vorgabeSatz}
            projekteVerfuegbar={projekteVerfuegbar}
            kopfProjekt={{
              projectId: beleg.projectId,
              projectNumber: beleg.projectNumber,
              projectPositionId: beleg.projectPositionId,
              projectPositionNumber: beleg.projectPositionNumber,
              projectPositionName: beleg.projectPositionName,
            }}
            artikelRolle={f.direction === 'PURCHASE' ? 'purchase' : f.direction === 'SALES' ? 'sales' : undefined}
            auswahl={auswahl}
            onAuswahl={setAuswahl}
            onCopy={() => void kopieren()}
            onPaste={(nach) => void einfuegen(nach)}
            onFoto={(key, datei) => anhaengen(key, { fotoNeu: datei, fotoWeg: false })}
            onFotoEntfernen={(key) => {
              const z = finde(zeilen, key)?.zeile
              if (!z) return
              // Ein wartendes Foto fällt einfach weg; ein gespeichertes geht erst mit „Speichern".
              anhaengen(key, z.fotoNeu ? { fotoNeu: null } : { fotoWeg: true, fotoAusAblage: false })
            }}
            documentId={beleg.id}
            einheiten={einheiten}
            onDateien={(key, dateien) => {
              const z = finde(zeilen, key)?.zeile
              if (z) anhaengen(key, { dateienNeu: [...(z.dateienNeu ?? []), ...dateien] })
            }}
            onDateiEntfernen={(key, fileId) => {
              const z = finde(zeilen, key)?.zeile
              if (!z) return
              const wartend = wartendeDatei(fileId)
              anhaengen(
                key,
                wartend !== null
                  ? { dateienNeu: (z.dateienNeu ?? []).filter((_, i) => i !== wartend) }
                  : { dateienWeg: [...(z.dateienWeg ?? []), fileId] },
              )
            }}
            onDateiLaden={(key, datei) =>
              void mitLauf(async () => {
                const lineId = finde(zeilen, key)?.zeile.id
                if (!lineId) return
                const { data } = await documentsApi.lineFile(beleg.id, lineId, datei.id)
                downloadFile(data, datei.fileName, datei.mimeType)
              })
            }
          />
        </section>
      )}

      {/* ── 3. Summen ── */}
      {preise && preiseDerArt && !nurText && ergebnis && (
        <section className={`${karte} ml-auto max-w-md`}>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.sections.totals')}</h2>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt>{t('documents.fields.netTotal')}</dt>
              <dd className="tabular-nums">{formatBetrag(ergebnis.netTotal, beleg.currency)}</dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt>{t('documents.fields.documentDiscount')}</dt>
              <dd className="flex items-center gap-2">
                <input disabled={!editierbar} value={kopf.documentDiscountPercent} onChange={(e) => setzeKopf({ documentDiscountPercent: e.target.value })} className="w-16 rounded border border-input bg-background px-1 py-0.5 text-right text-sm" placeholder="%" />
                <span className="tabular-nums">{ergebnis.discountTotal ? `− ${formatBetrag(ergebnis.discountTotal, beleg.currency)}` : ''}</span>
              </dd>
            </div>
            {ergebnis.taxes.map((g) => (
              <div key={g.taxRate} className="flex justify-between text-muted-foreground">
                <dt>{kopf.taxProcedure === 'STANDARD' ? t('documents.totals.tax', { rate: zahlText(g.taxRate, 2), net: formatBetrag(g.netAmount, beleg.currency) }) : t(`documents.taxProcedures.${kopf.taxProcedure}`)}</dt>
                <dd className="tabular-nums">{formatBetrag(g.taxAmount, beleg.currency)}</dd>
              </div>
            ))}
            <div className="flex justify-between border-t border-border pt-1 text-base font-semibold">
              <dt>{t('documents.fields.grossTotal')}</dt>
              <dd className="tabular-nums">{formatBetrag(ergebnis.grossTotal, beleg.currency)}</dd>
            </div>
          </dl>
        </section>
      )}

      {/* ── 4. Konditionen ── */}
      <section className={karte}>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.sections.conditions')}</h2>
        {/* Nachtrag 5: Felder je Art (Lieferkonditionen, Liefertermin …) zuerst, dann Zahlung, Steuer, Währung, Sprache. */}
        <KindFields
          kind={beleg.kind}
          werte={kindWerte}
          setze={(patch) => setzeKopf(patch)}
          editierbar={editierbar}
          sprache={kopf.language}
          waehrung={beleg.currency}
          gesamtCent={preise && ergebnis ? Math.round(ergebnis.grossTotal * 100) : null}
          eingabe={eingabe}
          label={label}
        />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {f.payable && (
            <>
              <div>
                <label className={label}>{t('documents.fields.paymentTermsDays')}</label>
                <input disabled={!editierbar} value={kopf.paymentTermsDays} onChange={(e) => setzeKopf({ paymentTermsDays: e.target.value })} className={eingabe} inputMode="numeric" />
              </div>
              <div>
                <label className={label}>{t('documents.fields.dueDate')}</label>
                <input type="date" disabled={!editierbar} value={kopf.dueDate} onChange={(e) => setzeKopf({ dueDate: e.target.value })} className={eingabe} />
              </div>
              <div>
                <label className={label}>{t('documents.fields.discountDays')}</label>
                <input disabled={!editierbar} value={kopf.discountDays} onChange={(e) => setzeKopf({ discountDays: e.target.value })} className={eingabe} inputMode="numeric" />
              </div>
              {preise && (
                <div>
                  <label className={label}>{t('documents.fields.discountPercent')}</label>
                  <input disabled={!editierbar} value={kopf.discountPercent} onChange={(e) => setzeKopf({ discountPercent: e.target.value })} className={eingabe} inputMode="decimal" />
                </div>
              )}
            </>
          )}
          {f.prices === 'optional' && (
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <input type="checkbox" disabled={!editierbar} checked={kopf.printPrices} onChange={(e) => setzeKopf({ printPrices: e.target.checked })} />
              {t('documents.head.printPrices')}
            </label>
          )}
          {f.prices !== 'no' && (
            <>
              <div>
                <label className={label}>{t('documents.fields.taxProcedure')}</label>
                <select disabled={!editierbar} value={kopf.taxProcedure} onChange={(e) => setzeKopf({ taxProcedure: e.target.value as TaxProcedure })} className={eingabe}>
                  {TAX_PROCEDURES.map((v) => (
                    <option key={v} value={v}>
                      {t(`documents.taxProcedures.${v}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>{t('documents.fields.priceMode')}</label>
                <select disabled={!editierbar} value={kopf.priceMode} onChange={(e) => setzeKopf({ priceMode: e.target.value as PriceMode })} className={eingabe}>
                  {PRICE_MODES.map((v) => (
                    <option key={v} value={v}>
                      {t(`documents.priceModes.${v}`)}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div>
            <label className={label}>{t('documents.fields.currency')}</label>
            <input disabled={!editierbar} value={kopf.currency} maxLength={3} onChange={(e) => setzeKopf({ currency: e.target.value.toUpperCase() })} className={eingabe} />
          </div>
          <div>
            <label className={label}>{t('documents.fields.language')}</label>
            <select disabled={!editierbar} value={kopf.language} onChange={(e) => setzeKopf({ language: e.target.value })} className={eingabe}>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </div>
          {/* N-79: Druckeinstellung, kein Inhalt — bleibt nach dem Festschreiben änderbar wie die Notiz. */}
          <label className="flex items-center gap-2 self-end pb-2 text-sm" title={t('documents.head.printLetterheadHint')}>
            <input type="checkbox" disabled={!(editierbar || beleg.rights.notes)} checked={kopf.printLetterhead} onChange={(e) => setzeKopf({ printLetterhead: e.target.checked })} />
            {t('documents.head.printLetterhead')}
          </label>
        </div>
        {kopf.taxProcedure !== 'STANDARD' && f.prices !== 'no' && (
          <div className="mt-3">
            <div className="flex items-center justify-between">
              <label className={label}>{t('documents.fields.taxNote')}</label>
              {/* Nachtrag: Die Vorgabetexte je Verfahren stehen in der Einrichtung → Textbausteine. */}
              {erfuelltRecht('correspondence.settings.manage', user?.permissions) && (
                <Link to="/correspondence/settings/texts" className="mb-1 text-xs text-primary hover:underline">
                  {t('documents.head.taxTextsLink')}
                </Link>
              )}
            </div>
            <textarea disabled={!editierbar} rows={2} value={kopf.taxNote} onChange={(e) => setzeKopf({ taxNote: e.target.value })} className={eingabe} />
          </div>
        )}
      </section>

      {/* ── 5. Texte ── */}
      <section className={karte}>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.sections.texts')}</h2>
        <div className="grid gap-3 lg:grid-cols-2">
          <div>
            <label className={label}>{t('documents.fields.headerText')}</label>
            <textarea disabled={!editierbar} rows={3} value={kopf.headerText} onChange={(e) => setzeKopf({ headerText: e.target.value })} className={eingabe} />
          </div>
          <div>
            <label className={label}>{t('documents.fields.footerText')}</label>
            <textarea disabled={!editierbar} rows={3} value={kopf.footerText} onChange={(e) => setzeKopf({ footerText: e.target.value })} className={eingabe} />
          </div>
          {beleg.kind === 'CUSTOM' && (
            <div className="lg:col-span-2">
              <label className={label}>{t('documents.fields.bodyText')}</label>
              <MarkdownField disabled={!editierbar} rows={10} value={kopf.bodyText} onChange={(v) => setzeKopf({ bodyText: v })} className={eingabe} />
              <p className="mt-1 text-xs text-muted-foreground">{t('documents.hints.markdownSubset')}</p>
            </div>
          )}
        </div>
      </section>

      {/* ── Nachtrag 6: Verrechnung des Lieferscheins ── */}
      {beleg.billing && <DeliveryBillingBlock beleg={beleg} onChange={anwenden} />}

      {/* ── 6. Kette & Verlauf ── */}
      <section className={karte}>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.sections.chain')}</h2>
        <div className="grid gap-4 text-sm md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold text-muted-foreground">{t('documents.chain.sources')}</p>
            {beleg.chain.sources.length === 0 && <p className="text-muted-foreground">—</p>}
            {beleg.chain.sources.map((d) => (
              <Link key={d.id} to={`/correspondence/${d.id}`} className="block text-primary hover:underline">
                {t(`documents.kinds.${d.kind}`)} {d.label}
              </Link>
            ))}
            {beleg.chain.cancels && (
              <Link to={`/correspondence/${beleg.chain.cancels.id}`} className="block text-primary hover:underline">
                {t('documents.chain.cancels')} {beleg.chain.cancels.label}
              </Link>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground">{t('documents.chain.targets')}</p>
            {beleg.chain.targets.length === 0 && beleg.chain.cancelledBy.length === 0 && <p className="text-muted-foreground">—</p>}
            {[...beleg.chain.targets, ...beleg.chain.cancelledBy].map((d) => (
              <Link key={d.id} to={`/correspondence/${d.id}`} className="block text-primary hover:underline">
                {t(`documents.kinds.${d.kind}`)} {d.label} · {t(`documents.statuses.${d.status}`)}
              </Link>
            ))}
          </div>
          {beleg.dispatches.length > 0 && (
            <div className="md:col-span-2">
              <p className="text-xs font-semibold text-muted-foreground">{t('documents.chain.dispatches')}</p>
              {beleg.dispatches.map((d) => (
                <div key={d.id}>
                  <p className={d.error ? 'text-destructive' : ''}>
                    {new Date(d.sentAt).toLocaleString(navigator.language)} · {d.channel} {d.recipient ?? ''} {d.error ? `— ${d.error}` : ''}
                  </p>
                  {/* AP-10 Nachtrag 1: über welchen Weg, von welcher Adresse */}
                  {d.transport && (
                    <p className="pl-4 text-xs text-muted-foreground">
                      {t(`documents.dispatch.via.${d.transport === 'MICROSOFT_365' ? 'MICROSOFT_365' : 'RESEND'}`)}
                      {d.senderAddress ? ` · ${t('documents.dispatch.from', { address: d.senderAddress })}` : ''}
                    </p>
                  )}
                  {/* AP-32 Nachtrag 10: eigene Dateien, die mit dieser Mail hinausgingen */}
                  {(d.extraFiles ?? []).map((f, i) => (
                    <p key={`${f.fileName}-${i}`} className="pl-4 text-xs text-muted-foreground">
                      📎 {f.fileName} ({f.sizeBytes >= 1024 * 1024 ? `${(f.sizeBytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(f.sizeBytes / 1024))} KB`}) · {t('documents.dispatch.ownFile')}
                    </p>
                  ))}
                  {/* AP-10 Nachtrag 2: Downloadlinks dieses Versands */}
                  {(d.links ?? []).map((l) => (
                    <p key={l.id} className="flex flex-wrap items-center gap-x-2 pl-4 text-xs text-muted-foreground">
                      <span>
                        🔗 {l.fileName}
                        {/* AP-10 Nachtrag 3: ein ZIP-Link trägt mehrere Dateien */}
                        {l.fileCount ? ` (${t('documents.links.zipFiles', { count: l.fileCount })})` : ''}
                      </span>
                      <span>
                        {l.status === 'ACTIVE'
                          ? // Der Ablauf liegt am Tagesende UTC — der Tag zählt, nicht die Ortszeit (wie in der Mail).
                            t('documents.links.activeUntil', { date: new Date(`${l.expiresAt.slice(0, 10)}T12:00:00`).toLocaleDateString(navigator.language) })
                          : t(`documents.links.status.${l.status}`)}
                      </span>
                      <span>· {t('documents.links.downloads', { count: l.downloadCount })}</span>
                      {l.status === 'ACTIVE' && beleg.rights.send && (
                        <button
                          type="button"
                          className="text-destructive hover:underline"
                          onClick={async () => {
                            if (!(await bestaetige(t('documents.links.revokeConfirm', { name: l.fileName })))) return
                            documentsApi
                              .revokeLink(beleg.id, l.id)
                              .then(({ data }) => anwenden(data.data))
                              .catch((err) => void hinweis(fehlertext(err, t)))
                          }}
                        >
                          {t('documents.links.revoke')}
                        </button>
                      )}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          )}
          <PaymentsBlock beleg={beleg} onChange={anwenden} />
        </div>
      </section>

      {/* ── 7. Intern ── */}
      <section className={karte}>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.sections.internal')}</h2>
        <label className={label}>{t('documents.fields.notes')}</label>
        <textarea disabled={!beleg.rights.notes} rows={3} value={kopf.notes} onChange={(e) => setzeKopf({ notes: e.target.value })} className={eingabe} />
        <p className="mt-1 text-xs text-muted-foreground">{t('documents.hints.notesInternal')}</p>
      </section>

      <SendDialog open={sendenOffen} beleg={beleg} onClose={() => setSendenOffen(false)} onSent={anwenden} />
      <DeductionsDialog open={abzugOffen} beleg={beleg} onClose={() => setAbzugOffen(false)} onApplied={anwenden} />
    </div>
  )
}
